import { useState } from 'react';
import { api, errorMessage } from '../../../api';
import { fmt } from '../../../format';
import type { CartonType, LocationView, PackingStation, WarehouseView } from '../../../types';
import { Empty, ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../../ui';
import { Icon } from '../../../ui/icons';

const PRINTERS: Readonly<Record<PackingStation['labelPrinter']['type'], string>> = {
  browser: 'Bu bilgisayarın yazıcısı',
  agent: 'Yazdırma ajanı',
  raw_tcp: 'Ağ yazıcısı (ZPL)',
};

/**
 * Packing tables (stations) and the cartons packers choose from. A packing table added on the
 * floor plan opens its station by itself; here they are renamed, switched off or added by hand.
 */
export function PackingSetup({ warehouse }: { warehouse: WarehouseView }) {
  const stations = useLoad(() => api<PackingStation[]>('GET', `/api/wms/stations?warehouseId=${warehouse.id}`), [warehouse.id]);
  const cartons = useLoad(() => api<CartonType[]>('GET', `/api/wms/cartons?warehouseId=${warehouse.id}`), [warehouse.id]);
  const tables = useLoad(() => api<LocationView[]>('GET', `/api/admin/warehouses/${warehouse.id}/locations?type=packing`), [warehouse.id]);
  const [station, setStation] = useState<PackingStation | 'new' | null>(null);
  const [carton, setCarton] = useState<CartonType | 'new' | null>(null);
  const tableCode = (id: string | null) => (id ? (tables.data?.find((t) => t.id === id)?.code ?? '—') : '—');

  return (
    <div className="stack">
      <section className="card">
        <div className="card-h">
          <Icon name="box" />
          <h2>Paketleme masaları</h2>
          <span className="sub">Paketleme ekranında masa seçilir; etiketler masanın yazıcısından basılır</span>
          <span className="spacer" />
          <button className="btn sm" onClick={() => setStation('new')}>
            <Icon name="plus" size={16} /> Masa ekle
          </button>
        </div>
        {stations.error && <ErrorBanner message={stations.error} onRetry={() => void stations.reload()} />}
        {!stations.data ? (
          <Spinner />
        ) : stations.data.length === 0 ? (
          <Empty title="Paketleme masası yok">Harita sekmesinde “Alan ekle → Paketleme masası” ile ekleyin; masa aynı kodla burada açılır.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Ad</th>
                  <th>Haritadaki yeri</th>
                  <th>Etiket yazıcısı</th>
                  <th>Durum</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {stations.data.map((s) => (
                  <tr key={s.id} className={s.isActive ? '' : 'muted-row'}>
                    <td className="mono">
                      <b>{s.code}</b>
                    </td>
                    <td>{s.name}</td>
                    <td className="mono">{tableCode(s.locationId)}</td>
                    <td>{PRINTERS[s.labelPrinter.type]}</td>
                    <td>{s.isActive ? <span className="chip ok">Açık</span> : <span className="chip">Kapalı</span>}</td>
                    <td>
                      <button className="btn sm ghost" onClick={() => setStation(s)} aria-label={`${s.code} masasını düzenle`}>
                        <Icon name="edit" size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-h">
          <Icon name="box" />
          <h2>Koli tipleri</h2>
          <span className="sub">Paketleyici koliyi seçer; kargo desisi koli ölçüsünden hesaplanır</span>
          <span className="spacer" />
          <button className="btn sm" onClick={() => setCarton('new')}>
            <Icon name="plus" size={16} /> Koli ekle
          </button>
        </div>
        {cartons.error && <ErrorBanner message={cartons.error} onRetry={() => void cartons.reload()} />}
        {!cartons.data ? (
          <Spinner />
        ) : cartons.data.length === 0 ? (
          <Empty title="Koli tipi yok">Kullandığınız koli ve poşet ölçülerini ekleyin (ör. K1 Küçük koli 20 × 15 × 10 cm).</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Ad</th>
                  <th>Ölçü (cm)</th>
                  <th className="num">Desi</th>
                  <th className="num">Dara</th>
                  <th>Durum</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cartons.data.map((c) => (
                  <tr key={c.code} className={c.isActive ? '' : 'muted-row'}>
                    <td className="mono">
                      <b>{c.code}</b>
                    </td>
                    <td>{c.name}</td>
                    <td>
                      {fmt.dec(c.lengthMm / 10)} × {fmt.dec(c.widthMm / 10)} × {fmt.dec(c.heightMm / 10)}
                    </td>
                    <td className="num">{fmt.dec(c.desi)}</td>
                    <td className="num">{c.tareWeightG ? `${fmt.n(c.tareWeightG)} g` : '—'}</td>
                    <td>{c.isActive ? <span className="chip ok">Kullanımda</span> : <span className="chip">Kapalı</span>}</td>
                    <td>
                      <button className="btn sm ghost" onClick={() => setCarton(c)} aria-label={`${c.code} kolisini düzenle`}>
                        <Icon name="edit" size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {station && (
        <StationDialog
          warehouse={warehouse}
          station={station === 'new' ? null : station}
          tables={tables.data ?? []}
          onClose={() => setStation(null)}
          onSaved={() => {
            setStation(null);
            void stations.reload();
          }}
        />
      )}
      {carton && (
        <CartonDialog
          warehouse={warehouse}
          carton={carton === 'new' ? null : carton}
          onClose={() => setCarton(null)}
          onSaved={() => {
            setCarton(null);
            void cartons.reload();
          }}
        />
      )}
    </div>
  );
}

function StationDialog(props: { warehouse: WarehouseView; station: PackingStation | null; tables: readonly LocationView[]; onClose: () => void; onSaved: () => void }) {
  const { warehouse, station } = props;
  const toast = useToast();
  const [code, setCode] = useState(station?.code ?? '');
  const [name, setName] = useState(station?.name ?? '');
  const [locationId, setLocationId] = useState(station?.locationId ?? props.tables[0]?.id ?? '');
  const [active, setActive] = useState(station?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api('PUT', `/api/wms/stations/${encodeURIComponent(code)}`, {
        warehouseId: warehouse.id,
        name: name.trim(),
        locationId: locationId || null,
        ...(station ? { labelPrinter: station.labelPrinter, documentPrinter: station.documentPrinter } : {}),
        isActive: active,
      });
      toast(`${code} masası kaydedildi`, 'ok');
      props.onSaved();
    } catch (e) {
      toast(errorMessage(e), 'bad');
      setBusy(false);
    }
  };
  return (
    <Modal
      title={station ? `Masa ${station.code}` : 'Yeni paketleme masası'}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !/^[A-Z0-9-]{2,20}$/.test(code) || !name.trim()} onClick={() => void save()}>
            Kaydet
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label htmlFor="st-code">Masa kodu</label>
          <input id="st-code" className="input mono" value={code} disabled={station !== null} maxLength={20} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} placeholder="PK-03" />
          <span className="hint">Paketleyici masayı bu kodla seçer</span>
        </div>
        <div className="field">
          <label htmlFor="st-name">Adı</label>
          <input id="st-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Paketleme masası 3" />
        </div>
        <div className="field">
          <label htmlFor="st-table">Haritadaki yeri</label>
          <select id="st-table" className="select" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">Seçilmedi</option>
            {props.tables.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code}
              </option>
            ))}
          </select>
        </div>
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Masa açık
        </label>
      </div>
      <p className="muted" style={{ fontSize: 13 }}>
        Yazıcı ayarı paketleme ekranındaki masa kartından yapılır (Yazıcı → bu bilgisayarın yazıcısı, ağ yazıcısı veya yazdırma ajanı).
      </p>
    </Modal>
  );
}

function CartonDialog(props: { warehouse: WarehouseView; carton: CartonType | null; onClose: () => void; onSaved: () => void }) {
  const { warehouse, carton } = props;
  const toast = useToast();
  const cm = (mm: number | undefined) => (mm === undefined ? '' : String(mm / 10).replace('.', ','));
  const [code, setCode] = useState(carton?.code ?? '');
  const [name, setName] = useState(carton?.name ?? '');
  const [length, setLength] = useState(cm(carton?.lengthMm));
  const [width, setWidth] = useState(cm(carton?.widthMm));
  const [height, setHeight] = useState(cm(carton?.heightMm));
  const [tare, setTare] = useState(carton?.tareWeightG ? String(carton.tareWeightG) : '');
  const [active, setActive] = useState(carton?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const mm = (text: string) => {
    const v = Number(text.trim().replace(',', '.'));
    return Number.isFinite(v) && v > 0 ? Math.round(v * 10) : null;
  };
  const dims = [mm(length), mm(width), mm(height)];
  const ok = /^[A-Z0-9-]{1,20}$/.test(code) && name.trim() && dims.every((d) => d !== null && d <= 3000);
  const desi = dims.every((d) => d !== null) ? (dims[0]! * dims[1]! * dims[2]!) / 1000 / 3000 : null;
  const save = async () => {
    setBusy(true);
    try {
      await api('PUT', `/api/wms/cartons/${encodeURIComponent(code)}`, {
        warehouseId: warehouse.id,
        name: name.trim(),
        lengthMm: dims[0],
        widthMm: dims[1],
        heightMm: dims[2],
        tareWeightG: tare.trim() ? Math.round(Number(tare.replace(',', '.'))) : 0,
        isActive: active,
      });
      toast(`${code} kaydedildi`, 'ok');
      props.onSaved();
    } catch (e) {
      toast(errorMessage(e), 'bad');
      setBusy(false);
    }
  };
  return (
    <Modal
      title={carton ? `Koli ${carton.code}` : 'Yeni koli tipi'}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !ok} onClick={() => void save()}>
            Kaydet
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label htmlFor="ct-code">Kod</label>
          <input id="ct-code" className="input mono" value={code} disabled={carton !== null} maxLength={20} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} placeholder="K1" />
        </div>
        <div className="field">
          <label htmlFor="ct-name">Adı</label>
          <input id="ct-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Küçük koli" />
        </div>
        <div className="field">
          <label>Ölçü (cm): uzunluk × genişlik × yükseklik</label>
          <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
            <input className="input" inputMode="decimal" value={length} onChange={(e) => setLength(e.target.value)} aria-label="Uzunluk (cm)" />
            <span>×</span>
            <input className="input" inputMode="decimal" value={width} onChange={(e) => setWidth(e.target.value)} aria-label="Genişlik (cm)" />
            <span>×</span>
            <input className="input" inputMode="decimal" value={height} onChange={(e) => setHeight(e.target.value)} aria-label="Yükseklik (cm)" />
          </div>
          {desi !== null && <span className="hint">Desi: {fmt.dec(Math.round(desi * 100) / 100)}</span>}
        </div>
        <div className="field">
          <label htmlFor="ct-tare">Boş ağırlığı (gram)</label>
          <input id="ct-tare" className="input" inputMode="numeric" value={tare} onChange={(e) => setTare(e.target.value.replace(/[^\d]/g, ''))} placeholder="120" />
        </div>
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Kullanımda
        </label>
      </div>
    </Modal>
  );
}
